/**
 * Seminar ↔ WordPress synchronization — the wired-in WP sync logic, driven by
 * the seminar store (syncSeminarToWp / syncReferentsToWp).
 *
 * Lifted out of React (out of the former useSeminarOperations hook) so the store
 * can call it directly: `raumeSelection` and `wpToken` arrive as arguments
 * instead of hook closures. WP sync is best-effort — failures rethrow for the
 * caller to log-and-continue, they are not toasted here. The WordPress
 * request shaping (FormData fields, ACF keys, Berlin/Brandenburg merge, Online
 * handling) is unchanged.
 */
import { referentApi, wordpressApi } from "../endpoints";
import { formatTime } from "../../utils/dateUtils";
import { ONLINE_RAUM_ID } from "../../constants/rooms";
import { relationId } from "../../utils/identity";
import type { Raeume, Seminar } from "../../types/entities";
import type { WpPostResult } from "../serializers/seminar";

/**
 * The write payload syncSeminarToWp shapes into the WordPress request.
 *
 * For `table: 'seminar'` this is the seminar entity — its fields drive the ACF
 * form. `id_wp_entry` is widened to accept the string post-ids the store/WP
 * bridge pass, and the WP-only columns not (yet) on {@link Seminar} the seminar
 * branch reads (lang_thema, preis, the audience/participation/focus fields) are
 * declared here. For `table: 'semref'` only `{ id_wp_entry, html }` is read.
 */
interface WpSeminarPayload extends Omit<Partial<Seminar>, "id_wp_entry" | "participants"> {
  id_wp_entry?: number | string | null;
  html?: string;
  lang_thema?: string;
  preis?: string | number | null;
  participants?: string | number | null;
  conditions_of_participation?: string;
  focus_areas?: string;
  audiences?: string | number | null;
}

/**
 * The populated referent fields the WP speaker HTML reads. `bemerkung` (a free
 * remark) is a real runtime referent column not yet modelled on the partial
 * Referent type, so it is declared here.
 */
interface WpSpeaker {
  vorname?: string;
  name?: string;
  bemerkung?: string;
}

interface SyncSeminarArgs {
  data: WpSeminarPayload;
  table?: string;
  raumeSelection?: Raeume[];
  wpToken: string;
}

interface SyncReferentsArgs {
  seminarId: number | string | undefined;
  id_wp_entry: number | string | null | undefined;
  raumeSelection?: Raeume[];
  wpToken: string;
}

/**
 * Update (or create) a WordPress seminar / referent post.
 *
 * @param {object}   args
 * @param {object}   args.data            - the seminar (table 'seminar') or
 *                                          `{ id_wp_entry, html }` (table 'semref')
 * @param {'seminar'|'semref'} [args.table='seminar']
 * @param {Array}    [args.raumeSelection=[]] - rooms list (['rooms'] query data)
 * @param {string}   args.wpToken         - WordPress bearer token
 * @returns {Promise<object|undefined>} the WP post result data on success
 */
export async function syncSeminarToWp({
  data,
  table = "seminar",
  raumeSelection = [],
  wpToken,
}: SyncSeminarArgs): Promise<WpPostResult | undefined> {
  const wptoken = wpToken;
  const formdata = new FormData();

  if (table === "seminar") {
    const {
      id,
      raum,
      thema,
      lang_thema,
      preis,
      datum,
      anfangs_zeit,
      end_datum,
      end_zeit,
      participants,
      conditions_of_participation,
      focus_areas,
      audiences,
      wp_published,
    } = data;

    const raumId = relationId(raum);
    const findraume = raumeSelection.find((raume) => raume.id === raumId);

    formdata.append("title", `${!thema ? "" : thema}`);
    formdata.append("content", `${!lang_thema ? "" : lang_thema}`);
    formdata.append("status", `${wp_published ? "publish" : "draft"}`);
    formdata.append("acf[most_popular]", "0");
    formdata.append("acf[seminar_id]", `${id}`);
    formdata.append("acf[price]", `${preis}`);
    formdata.append("acf[start_date]", `${formatTime(datum ?? "", "date")}`);
    formdata.append("acf[start_time]", `${formatTime(anfangs_zeit ?? "", "time")}`);
    formdata.append("acf[end_date]", `${formatTime(end_datum ?? "", "date")}`);
    formdata.append("acf[end_time]", `${formatTime(end_zeit ?? "", "time")}`);
    formdata.append("acf[participants]", `${!participants ? "" : participants}`);
    formdata.append(
      "acf[conditions_of_participation]",
      `${!conditions_of_participation ? "" : conditions_of_participation}`
    );
    formdata.append("acf[focus_areas]", `${!focus_areas ? "" : focus_areas}`);

    if (findraume) {
      let stateid = findraume?.bundesland_id || "";

      // Merge Berlin (id 45) and Brandenburg (id 5) as per WordPress backend
      if (stateid === "45") {
        stateid = "5";
      }

      formdata.append("acf[city]", `${findraume.ort}`);
      formdata.append(
        "acf[location]",
        findraume.id === ONLINE_RAUM_ID
          ? "Online"
          : `${findraume.bezeichnung || ""}, ${findraume.strasse || ""} ${findraume.haus_nr || ""}, ${findraume.plz || ""}, ${findraume.ort || ""}`
      );

      formdata.append("terms[state][]", `${findraume.id !== ONLINE_RAUM_ID ? stateid : ""}`);
    }

    formdata.append("terms[audience]", `${audiences || ""}`);
  }

  if (table === "semref") {
    formdata.append("acf[speaker]", data.html || "");
  }

  try {
    const result = await wordpressApi.updateSeminar(data?.id_wp_entry || "", formdata, wptoken);

    if (result?.data) {
      return result.data;
    }
  } catch (err) {
    // WordPress sync is best-effort: rethrow so the caller can log-and-continue.
    // Do NOT notify here — it double-toasted with the caller's own error handling
    // and (in updateSeminar) hid the "Seminar gespeichert" success even though the
    // seminar had already persisted in Strapi.
    throw err;
  }
}

/**
 * Rebuild the referent HTML for a seminar and push it to its WordPress post.
 * Ported from `addReferenteToWPSite`.
 *
 * @param {object} args
 * @param {number|string} args.seminarId   - numeric seminar id (junction filter)
 * @param {number|string} args.id_wp_entry - the seminar's WP post id
 * @param {Array}  [args.raumeSelection=[]]
 * @param {string} args.wpToken
 */
export async function syncReferentsToWp({
  seminarId,
  id_wp_entry,
  raumeSelection = [],
  wpToken,
}: SyncReferentsArgs): Promise<void> {
  const seminarReferenteResult = await referentApi.getBySeminar(seminarId);
  const seminarReferenteResultData = seminarReferenteResult?.data?.data;

  const referentHtmlGen = (seminarReferenteResultData || [])
    .filter((referent) => referent?.id_referent)
    .map((referent) => {
      const ref = referent.id_referent;
      const { vorname, name, bemerkung }: WpSpeaker = ref && typeof ref === "object" ? ref : {};
      return `<div style="padding-bottom: 10px"><strong style="padding-bottom: 10px">${vorname || ""} ${name || ""}</strong>${bemerkung || ""}</div>\n    `;
    });

  if (seminarId) {
    await syncSeminarToWp({
      data: {
        id_wp_entry,
        html: (seminarReferenteResultData?.length ?? 0) === 0 ? "" : referentHtmlGen.join(""),
      },
      table: "semref",
      raumeSelection,
      wpToken,
    });
  }
}
